import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import {
  LightOrderSheet,
  LightOrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import React, { useMemo, useState } from "react";
import AppTable, { AppTableCell } from "@/features/line-sheet-sets/app-table";
import { Box, Flex, Text } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import SortableTableHeader, {
  cycleSortDirection,
  SortDirection,
} from "@/features/line-sheet-sets/detail/sortable-table-header";

interface ConfirmedConditionPanelProps {
  orderSheetSet?: LightOrderSheetSet;
}

export default function ConfirmedConditionPanel({
  orderSheetSet,
}: ConfirmedConditionPanelProps) {
  const { tTitle } = useI18nHelper();
  // 기본은 접힌 상태 — 행이 많을 때 화면 공간 절약.
  const [isCollapsed, setIsCollapsed] = useState(true);
  // final_condition(값) 컬럼 정렬 상태. none이면 원본 순서 유지.
  const [sortDir, setSortDir] = useState<SortDirection>("none");

  const conditions = useMemo(() => {
    if (
      orderSheetSet &&
      orderSheetSet.boutiqueConfirmationMetadataList &&
      orderSheetSet.boutiqueConfirmationMetadataList.length > 0
    ) {
      const { boutiqueConfirmationMetadataList: metadataList } = orderSheetSet;
      return metadataList.flatMap((metadata) => metadata.conditions);
    }
    return undefined;
  }, [orderSheetSet]);

  const rows = useMemo((): AppTableCell<any>[][] | undefined => {
    if (!conditions) return undefined;

    const ordered =
      sortDir === "none"
        ? conditions
        : [...conditions].sort((a, b) =>
            sortDir === "asc" ? a.value - b.value : b.value - a.value
          );

    return ordered.map((condition) => {
      return [
        {
          value: condition.brand.name,
        },
        {
          value: condition.gender,
        },
        {
          value: condition.category,
        },
        {
          value: condition.type,
        },
        {
          value: (
            <Text textAlign={"right"}>
              {" "}
              {condition.value}%
            </Text>
          ),
        },
      ];
    });
  }, [conditions, sortDir]);

  if (!orderSheetSet || !rows) {
    return null;
  }

  // 접었을 때 상위 N개는 항상 유지. 행이 N개 이하면 접을 게 없어 토글도 숨김.
  const VISIBLE_WHEN_COLLAPSED = 3;
  const isCollapsible = rows.length > VISIBLE_WHEN_COLLAPSED;
  const displayRows =
    isCollapsible && isCollapsed
      ? rows.slice(0, VISIBLE_WHEN_COLLAPSED)
      : rows;

  return (
    <AppPanel title={tTitle("condition")} isCompact={true}>
      <AppTable
        headers={[
          {
            value: tTitle("common\:brand"),
            width: "minmax(88px, 1fr)",
          },
          {
            value: tTitle("common\:gender"),
            width: "minmax(88px, 172px)",
          },
          {
            value: tTitle("category"),
            width: "minmax(88px, 172px)",
          },
          {
            value: tTitle("type"),
            width: "minmax(88px, 172px)",
          },
          {
            value: (
              <SortableTableHeader
                label={tTitle("final_condition")}
                direction={sortDir}
                onClick={() => setSortDir((d) => cycleSortDirection(d))}
              />
            ),
            width: "minmax(88px, 172px)",
            textAlign: "right",
          },
        ]}
        rows={displayRows}
        rowHeight={"32px"}
        gap={"8px"}
        padding={"0 32px"}
      />
      {/* 테이블 아래 토글 — 행이 3개 이상일 때만 노출 */}
      {isCollapsible && (
        <Flex
          as={"button"}
          type={"button"}
          width={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"4px"}
          padding={"8px"}
          borderTop={!isCollapsed ? "0.5px solid #E5E5E5" : "none"}
          color={"#6F6F6C"}
          fontSize={"12px"}
          cursor={"pointer"}
          userSelect={"none"}
          _hover={{ color: "#1272EF", background: "#F7F9FA" }}
          onClick={() => setIsCollapsed((v) => !v)}
          aria-expanded={!isCollapsed}
        >
          <Text>
            {isCollapsed
              ? `펼치기 (+${rows.length - VISIBLE_WHEN_COLLAPSED})`
              : "접기"}
          </Text>
          <Box
            display={"flex"}
            transform={isCollapsed ? "rotate(0deg)" : "rotate(180deg)"}
            transition={"transform 0.15s"}
          >
            <SVG
              src={"/icons-v1_1/chevron-down.svg"}
              width={"12"}
              height={"12"}
            />
          </Box>
        </Flex>
      )}
    </AppPanel>
  );
}
